import type {Config, Driver} from 'driver.js';
import type {Container} from '../../container.ts';
import type {OnboardingConfig, OnboardingProgress, OnboardingSectionName} from './types.ts';
import {completed, uncompleted} from './icons.ts';
import {dashboardConfig} from './dashboardConfig.ts';
import {completeOnboarding, getInstanceBaseUrl} from './utils.ts';

/**
 * Renders a human-readable date based on a Unix timestamp.
 *
 * dateTime - The Unix timestamp to be rendered.
 */
function renderDate(dateTime: number): string {
  const unixTimestamp = dateTime * 1000;
  const date = new Date(unixTimestamp);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const formattedDate = date.toLocaleString(undefined, options);

  return formattedDate;
}

/**
 * Generates the configuration object for the introductory
 * onboarding wizard modal using Driver.js.
 *
 * @param {Driver} driverObj - The Driver.js instance for creating the wizard.
 * @param {Object} onboardingProgress - An object representing the user's onboarding progress.
 * @param {Container} container - The dependency injection providing access to necessary services.
 * @returns {Object} TThe configuration object for the intro-specific onboarding.
 */
export function introConfig(driverObj: Driver, onboardingProgress: OnboardingProgress, container: Container): OnboardingConfig {
  const {assetUrlPrefix, csrfToken} = window.config;
  const appSubUrl = container.lookup('appSubUrl');

  function getStatusIcon(section: OnboardingSectionName) {
    if (onboardingProgress.sections?.[section]?.completed) {
      return completed;
    }
    return uncompleted;
  }

  function getCompletionDate(section: OnboardingSectionName) {
    const lastCompletion =
      onboardingProgress.sections?.[section]?.last_completion;
    const isSkipped = onboardingProgress.sections?.[section]?.skipped;
    const isCompleted = onboardingProgress.sections?.[section]?.completed;

    if (isSkipped || !isCompleted) {
      return '';
    }

    return `<span class="completion-date">${renderDate(lastCompletion)}</span>`;
  }

  return {
    showProgress: false,
    smoothScroll: true,
    allowClose: true,
    popoverClass: 'intro-popover',
    name: 'Intro',
    onDestroyStarted: () => {
      completeOnboarding('Intro', csrfToken, 0, true, container);
      driverObj.destroy();
    },
    steps: [
      {
        popover: {
          description: `<div>
              <img class="logo" width="120" src="${assetUrlPrefix}/img/logo_blue_text.png"/>
              <h2>Welcome To AllSpice Hub!</h2>
              <p>Here are a few onboarding tours to help you get going right away. Remember, you can always continue where you leave off by clicking "Onboarding" in the top-right User Menu.</p>
              <p>To get started, select the first onboarding session below!</p>
              <ul class="onboarding-checklist">
                <li>
                  <button id="dashboardTrigger">
                    <div class="status-container">${getStatusIcon(
                      'dashboard',
                    )}</div>
                    <span class="">Dashboard Onboarding</span>
                    <span class="completion-date">
                    ${getCompletionDate('dashboard')}</span>
                  </button>
                </li>
              </ul>
            </div>`,
          showButtons: ['next', 'close'],
          nextBtnText: 'Skip',
          onPopoverRender: () => {
            document
              .querySelector('#dashboardTrigger')
              .addEventListener('click', () => {
                // if necessary navigate user to home page prior to
                // starting the onboarding wizard
                if (window.location.pathname !== '/') {
                  window.location.href = `${getInstanceBaseUrl(appSubUrl)}?onboarding=dashboard`;
                  return;
                }

                // update the config to the dashboard config
                driverObj.setConfig(
                  dashboardConfig(
                    driverObj,
                    introConfig,
                    onboardingProgress,
                    container,
                  ) as Config,
                );
                // start the driver again
                driverObj.drive();
              });
          },
          onNextClick: () => {
            // skip the onboarding wizard
            completeOnboarding('Intro', csrfToken, 0, true, container);
            driverObj.destroy();
          },
        },
      },
    ],
  };
}
